import { Mail, MessageSquarePlus, Phone } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router';

import { EmployeeJobCard } from '@/components/employee/EmployeeJobCard';
import { EmployeePage } from '@/components/employee/EmployeePage';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/empty-state';
import { FormError } from '@/components/ui/field';
import { useAddClientNote, useEmployeeClient } from '@/hooks/employee-portal';
import { ApiError } from '@/lib/api';
import { formatInstant } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';

/*
 * /employee/clients/:id — ROADMAP.md §6.7, and **the screen P22 was found on**.
 *
 * In the prototype this page asks for the client and then asks separately for
 * their jobs; the jobs call returns a 500, the component renders `data ?? []`,
 * and the result is "No jobs found for this client" sitting underneath a header
 * that reads "2 Total Jobs". A cleaner reads that and concludes the customer is
 * new.
 *
 * Two things make that unrepeatable here rather than merely fixed:
 *
 *   - **One request.** The jobs and the count come back together and the count
 *     is derived from the list, so they cannot disagree. There is no second
 *     endpoint to fail on its own.
 *   - **`api.ts` throws on any non-2xx**, so a failure arrives as `error` and
 *     this screen renders `ErrorState`. There is no path where a failed request
 *     looks like an empty one.
 *
 * No addresses and no access notes — business-rules.md §2 gives an employee
 * properties "assigned only", and the jobs below carry the addresses they were
 * actually sent to.
 */

export function EmployeeClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const client = useEmployeeClient(id);
  const detail = client.data;

  if (client.error) {
    const notFound = client.error instanceof ApiError && client.error.status === 404;

    return (
      <EmployeePage title="Client" back={{ to: '/employee/clients', label: 'Clients' }}>
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            title={notFound ? 'That client is not on our list' : 'We could not load this client'}
            hint={
              notFound
                ? 'They may have been archived. Ring the office if you were expecting them.'
                : 'Something went wrong on our side — this does not mean they have no history. Try again.'
            }
            {...(notFound
              ? {}
              : {
                  onRetry: () => {
                    void client.refetch();
                  },
                })}
          />
        </div>
      </EmployeePage>
    );
  }

  if (client.isPending || !detail) {
    return (
      <EmployeePage title="Client" back={{ to: '/employee/clients', label: 'Clients' }}>
        <div aria-busy="true" className="space-y-4">
          <span className="sr-only">Loading this client</span>
          {[0, 1].map((key) => (
            <div
              key={key}
              aria-hidden="true"
              className="h-40 animate-pulse rounded-lg bg-ink-100"
            />
          ))}
        </div>
      </EmployeePage>
    );
  }

  return (
    <EmployeePage
      title={detail.displayName}
      description={detail.displayName === detail.contactName ? undefined : detail.contactName}
      back={{ to: '/employee/clients', label: 'Clients' }}
    >
      <div className="space-y-4">
        <section className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
          <h2 className="text-h3 text-ink-900">Getting hold of them</h2>
          <dl className="mt-3 space-y-2 text-body">
            <div className="flex items-start gap-2">
              <dt className="sr-only">Phone</dt>
              <Phone aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-ink-500" />
              <dd className="text-ink-900">
                {detail.phone === null ? (
                  <span className="italic text-ink-500">No phone number on file</span>
                ) : (
                  <a
                    href={`tel:${detail.phone}`}
                    className="inline-flex min-h-touch items-center text-bark-700 underline underline-offset-2"
                  >
                    {detail.phone}
                  </a>
                )}
              </dd>
            </div>
            <div className="flex items-start gap-2">
              <dt className="sr-only">Email</dt>
              <Mail aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-ink-500" />
              <dd className="min-w-0 break-words text-ink-900">
                {detail.email ?? <span className="italic text-ink-500">No email on file</span>}
              </dd>
            </div>
          </dl>
          <p className="mt-3 text-meta text-ink-500">
            {/* Explain, don't hide — design-system.md §7. A cleaner who wonders
                where the address is should not have to ask. */}
            Addresses are on the jobs below. You see a property when you are sent to it.
          </p>
        </section>

        <section>
          <h2 className="mb-3 text-h2 text-ink-900">
            {/*
              **The count is `myJobs.length`.** It is not a separate figure that
              can outlive the list it describes — the whole shape of P22.
            */}
            Your jobs here ({detail.myJobs.length})
          </h2>

          {detail.myJobs.length === 0 ? (
            <div className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
              <p className="text-body text-ink-700">
                You have not worked for this customer yet. This is genuinely empty — if it looked
                like a loading problem, the screen would say so.
              </p>
            </div>
          ) : (
            <ul className="space-y-3">
              {detail.myJobs.map((entry) => (
                <EmployeeJobCard key={entry.id} job={entry} />
              ))}
            </ul>
          )}
        </section>

        <NotesCard clientId={detail.id} notes={detail.notes} />
      </div>
    </EmployeePage>
  );
}

/**
 * Notes — Q5's other half, and the prototype allows a cleaner to add them.
 *
 * The same `client_note` table the office writes to, so "the gate sticks" is in
 * one place rather than two. That also means it is visible on the admin client
 * screen, and the form says so: a note somebody thought was private is a note
 * they would have worded differently.
 */
function NotesCard({
  clientId,
  notes,
}: {
  clientId: string;
  notes: { id: string; body: string; authorName: string | null; createdAt: string }[];
}) {
  const toast = useToast();
  const add = useAddClientNote(clientId);
  const [body, setBody] = useState('');

  const error = add.error instanceof ApiError ? add.error : null;

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (body.trim() === '') return;

    add.mutate(body, {
      onSuccess: () => {
        setBody('');
        toast.success('Note added.');
      },
    });
  }

  return (
    <section className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <h2 className="flex items-center gap-1.5 text-h3 text-ink-900">
        <MessageSquarePlus aria-hidden="true" className="size-4 text-ink-500" />
        Notes
      </h2>
      <p className="mt-1 text-meta text-ink-500">
        Anything worth the next person knowing. The office sees these too.
      </p>

      <form onSubmit={submit} className="mt-3 space-y-2">
        {error ? <FormError>{error.message}</FormError> : null}

        <label htmlFor="client-note" className="sr-only">
          Add a note
        </label>
        <textarea
          id="client-note"
          name="body"
          rows={3}
          maxLength={5000}
          value={body}
          onChange={(event) => {
            setBody(event.target.value);
          }}
          placeholder="The gate sticks — lift it while you push."
          className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2 text-body"
        />
        <Button type="submit" variant="outline" disabled={add.isPending || body.trim() === ''}>
          {add.isPending ? 'Adding…' : 'Add note'}
        </Button>
      </form>

      {notes.length === 0 ? (
        <p className="mt-4 text-body text-ink-500">No notes yet.</p>
      ) : (
        <ul className="mt-4 space-y-3">
          {notes.map((note) => (
            <li key={note.id} className="rounded-sm border border-ink-300 p-3">
              <p className="whitespace-pre-wrap text-body text-ink-900">{note.body}</p>
              <p className="mt-1.5 text-meta text-ink-500">
                {note.authorName ?? 'Someone at the office'} · {formatInstant(note.createdAt)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
